<template>
  <div>
    <button class="btn btn-edit" @click="openModal">
      <img src="@/assets/images/admin/cashpayments/edit.png" alt="Edit" style="width: 25px; height: 25px;" />
    </button>

    <div class="modal fade" ref="editRoleModal" tabindex="-1" role="dialog" aria-labelledby="editRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title">Edit Role Permissions</h4>
            <button type="button" class="close" @click="hideModal" aria-label="Close">
              <span aria-hidden="true">X</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="row d-flex align-items-stretch">
              <div class="col-md-12 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <div class="form-group">
                      <label for="role-name">Role Name</label>
                      <input type="text" id="role-name" class="form-control" v-model="form.role_name" disabled />
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <div class="form-group">
                      <label>Permission Settings</label>
                      <div v-for="module in moduleTree" :key="module.id" class="permission-group">
                        <label class="checkbox-label">
                          <input 
                            type="checkbox" 
                            :checked="!!form.permissions[module.id]"
                            @change="onParentChange(module, $event.target.checked)"
                          >
                          <span class="checkmark"></span>
                          {{ module.name }}
                        </label>

                        <div
                          v-for="child in module.children"
                          :key="child.id"
                          class="custom-checkbox child-checkbox"
                        >
                          <label class="checkbox-label">
                            <input
                              type="checkbox"
                              :checked="!!form.permissions[child.id]"
                              @change="updatePermission(child.id, $event.target.checked)"
                            >
                            <span class="checkmark"></span>
                            {{ child.name }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end">
                <button class="btn btn-green btn-ok me-1" @click="submitForm">Save</button>
                <button class="btn btn-cancel" @click="hideModal">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import { useRoleStore } from '@/store/admin/role';

const props = defineProps({
  id: Number,
});

const roleStore = useRoleStore();
const editRoleModal = ref(null);
let modalInstance = null;

const moduleTree = ref([]);

const form = ref({
  role_id: null,
  role_name: '',
  permissions: {}
});

const updatePermission = (moduleId, value) => {
  form.value.permissions[moduleId] = value;
};

const normalizeChildren = (children = []) => {
  return children.map(child => ({
    id: child.id,
    name: child.name,
    permission: child.permission,
    children: normalizeChildren(child.children || [])
  }));
};

const buildModuleTree = (modulePermissions = []) => {
  const parentMap = new Map();

  modulePermissions.forEach(item => {
    const module = item.module;
    if (!module || module.parent_id !== null) return;

    parentMap.set(module.id, {
      id: module.id,
      name: module.name,
      permission: item.permission,
      children: normalizeChildren(module.children || [])
    });
  });

  return Array.from(parentMap.values());
};

const collectModuleIds = (modules = [], idSet = new Set()) => {
  modules.forEach(module => {
    idSet.add(module.id);
    if (module.children?.length) {
      collectModuleIds(module.children, idSet);
    }
  });
  return idSet;
};

const collectPermissionFromTree = (modules = [], permissionMap = new Map()) => {
  modules.forEach(module => {
    if (module.permission !== undefined && module.permission !== null) {
      permissionMap.set(module.id, Number(module.permission) === 1 || module.permission === true);
    }

    if (module.children?.length) {
      collectPermissionFromTree(module.children, permissionMap);
    }
  });

  return permissionMap;
};

const onParentChange = (parentModule, checked) => {
  updatePermission(parentModule.id, checked);

  const childIds = collectModuleIds(parentModule.children || []);
  childIds.forEach(childId => {
    updatePermission(childId, checked);
  });
};

const openModal = async () => {
  try {
    await roleStore.fetchRoleInfo(props.id);
    const roleInfo = roleStore.roleInfo;
    const modulePermissions = roleInfo?.module_permissions || [];
    moduleTree.value = buildModuleTree(modulePermissions);
    
    form.value = {
      role_id: roleInfo.id,
      role_name: roleInfo.name,
      permissions: {}
    };

    modulePermissions.forEach(permission => {
      form.value.permissions[permission.module_id] = permission.permission === 1;
    });

    // 後端若在 children 直接帶 permission，優先以樹狀權限回填子項目
    const treePermissions = collectPermissionFromTree(moduleTree.value);
    treePermissions.forEach((permission, moduleId) => {
      form.value.permissions[moduleId] = permission;
    });

    const allModuleIds = collectModuleIds(moduleTree.value);
    allModuleIds.forEach(moduleId => {
      if (form.value.permissions[moduleId] === undefined) {
        form.value.permissions[moduleId] = false;
      }
    });

    if (!modalInstance) {
      modalInstance = new Modal(editRoleModal.value);
    }
    modalInstance.show();
  } catch (error) {
    console.error('Error fetching role info:', error);
    Swal.fire({
      icon: 'error',
      title: 'Failed to get role information',
      text: error.message
    });
  }
};

const hideModal = () => {
  if (modalInstance) {
    modalInstance.hide();
  }
};

const submitForm = async () => {
  try {
    const allModuleIds = Array.from(collectModuleIds(moduleTree.value));
    const payload = {
      role_id: form.value.role_id,
      modules: allModuleIds.map(moduleId => ({
        module_id: moduleId,
        permission: !!form.value.permissions[moduleId]
      }))
    };

    await roleStore.updateRole(payload);
    emit('permissions-updated');
    hideModal();
  } catch (error) {
    console.error('Error updating role:', error);
    Swal.fire({
      icon: 'error',
      title: 'Failed to update role permissions',
      text: error.message
    });
  }
};

const emit = defineEmits(['permissions-updated']);
</script>

<style scoped>
label {
  font-size: 16px;
  margin-top: 5px;
}

.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 244.83px;
  height: 46px;
  border-radius: 30px;
  padding: 10px, 32px, 10px, 32px;
}

.btn-edit{
  color: #8476f5;
}

.btn-green:hover {
  background-color: #00b452;
}

.btn-ok {
  width: 150px;
}

.btn-cancel {
  background-color: #fff;
  color: #8476f4;
  border: 2px solid #8476f4;
  border-radius: 30px;
  width: 150px;
  height: 46px;
  padding: 14px 32px 14px 32px;
}

.btn-cancel:hover {
  background-color: #e6e6ff;
}

.modal-content {
  border-radius: 10px;
  padding: 20px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header {
  border-bottom: 1px solid #dee2e6;
  font-weight: bold;
  background-color: #d9dde3;
}

.card {
  background-color: #d9dde3;
}

.close {
  color: #333;
  opacity: 1;
  font-size: 20px;
  padding: 0;
  background: none;
  border: none;
}

.close:hover {
  color: #000;
}

.row.d-flex {
  display: flex;
}

.col-md-6.d-flex {
  display: flex;
  flex-direction: column;
}

.card.flex-grow-1 {
  flex-grow: 1;
}

.align-items-stretch {
  align-items: stretch;
}

.text-danger {
  color: red;
  font-size: 0.9em;
}

.custom-checkbox {
  margin: 10px 0;
}

.permission-group {
  margin: 10px 0;
}

.child-checkbox {
  margin-left: 30px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 35px;
  cursor: pointer;
  font-size: 16px;
  user-select: none;
}

.checkbox-label input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #fff;
  border: 2px solid #00c962;
  border-radius: 4px;
  transition: all 0.3s;
}

.checkbox-label:hover input ~ .checkmark {
  background-color: #f0f0f0;
}

.checkbox-label input:checked ~ .checkmark {
  background-color: #00c962;
  border-color: #00c962;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid black;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-label input:checked ~ .checkmark:after {
  display: block;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}
</style>
